import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoardFile } from '@os/doc';
import { useApi } from '../../app/providers';
import { canDelete, TEMPLATE_CATEGORIES, type Board, type Org, type TemplateCategory } from '../../lib/api';
import { Button } from '../../ui';
import { useSession } from '../auth/session';
import { BoardCard, Grid, Section } from '../dashboard/cards';
import { NameDialog } from '../dashboard/dialogs';
import { useInvalidateContent, useTemplates } from '../dashboard/queries';
import type { TemplateChoice } from './instantiate';
import { SYSTEM_TEMPLATES, systemTemplate, templateLabels, type SystemTemplateId } from './system';
import { TemplatePreview } from './TemplatePreview';
import { UseTemplateDialog } from './UseTemplateDialog';

/** The system templates' files in the current language (null for the blank board), built once per language. */
export function useSystemTemplateFiles(): ReadonlyMap<SystemTemplateId, BoardFile | null> {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  return useMemo(() => {
    const labels = templateLabels(t);
    return new Map(SYSTEM_TEMPLATES.map((s) => [s.id, s.id === 'blank' ? null : systemTemplate(s.id, labels)]));
  }, [t, language]);
}

export const choiceOf = (id: SystemTemplateId): TemplateChoice => (id === 'blank' ? { kind: 'blank' } : { kind: 'system', id });

function TemplateActions({ board, org, onUse }: { board: Board; org: Org; onUse: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const session = useSession();
  const invalidate = useInvalidateContent();
  const [renaming, setRenaming] = useState(false);
  const [error, setError] = useState(false);
  const userId = session.status === 'signed-in' ? session.user.id : '';
  const trash = async () => {
    setError(false);
    try {
      await api.lifecycle('board', 'trash', board.id);
      await invalidate();
    } catch {
      setError(true);
    }
  };
  return (
    <>
      <div className="flex flex-wrap gap-2 font-sans text-sm">
        <Button onClick={onUse}>
          {t('boardTemplates.use')}
        </Button>
        <Button onClick={() => setRenaming(true)}>
          {t('dashboard.rename')}
        </Button>
        {canDelete(org.role, board.createdBy, userId) && (
          <Button onClick={() => void trash()}>
            {t('dashboard.toTrash')}
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="font-sans text-xs text-danger">
          {t('auth.genericError')}
        </p>
      )}
      {renaming && (
        <NameDialog
          title={t('dashboard.renameBoard')}
          label={t('dashboard.boardTitle')}
          submitLabel={t('dashboard.save')}
          initial={board.title}
          max={200}
          onClose={() => setRenaming(false)}
          onSubmit={async (title) => {
            await api.updateBoard(board.id, { title });
            await invalidate();
          }}
        />
      )}
    </>
  );
}

/** "Templates" in the sidebar (spec §H): the studio's system templates, then the organization's own, by category. */
export function TemplatesPage({ org }: { org: Org }) {
  const { t } = useTranslation();
  const api = useApi();
  const files = useSystemTemplateFiles();
  const custom = useTemplates(org.id);
  const [category, setCategory] = useState<TemplateCategory | null>(null);
  const [using, setUsing] = useState<{ choice: TemplateChoice; name: string } | null>(null);
  const canCopy = api.capabilities.boardCopy;
  const system = SYSTEM_TEMPLATES.filter((s) => category === null || s.category === category);
  const ours = (custom.data ?? []).filter((b) => category === null || b.templateCategory === category);

  return (
    <>
      <h1 className="font-display text-display-md font-normal tracking-display text-fg">{t('dashboard.templates')}</h1>
      <div role="group" aria-label={t('boardTemplates.filter')} className="flex flex-wrap gap-2">
        {[null, ...TEMPLATE_CATEGORIES].map((c) => (
          <Button key={c ?? 'all'} aria-pressed={category === c} variant={category === c ? 'ghost' : 'secondary'} className={category === c ? 'bg-surface-2' : undefined} onClick={() => setCategory(c)}>
            {c ? t(`boardTemplates.categories.${c}`) : t('boardTemplates.all')}
          </Button>
        ))}
      </div>
      <Section title={t('boardTemplates.studio')} empty={system.length === 0} emptyBody={t('dashboard.emptyBody.templates')}>
        <Grid>
          {system.map((s) => (
            <article key={s.id} className="flex flex-col gap-2 rounded-lg bg-surface p-4 shadow-sm">
              <TemplatePreview file={files.get(s.id) ?? null} />
              <h3 className="font-display text-lg font-bold text-fg">{t(`boardTemplates.names.${s.id}`)}</h3>
              <p className="font-sans text-sm text-fg-muted">{t(`boardTemplates.descriptions.${s.id}`)}</p>
              {(canCopy || s.id === 'blank') && (
                <Button className="self-start" onClick={() => setUsing({ choice: choiceOf(s.id), name: t(`boardTemplates.names.${s.id}`) })}>
                  {t('boardTemplates.use')}
                </Button>
              )}
            </article>
          ))}
        </Grid>
      </Section>
      <Section title={t('boardTemplates.ours')} empty={custom.isSuccess && ours.length === 0} emptyBody={t('dashboard.emptyBody.templates')}>
        <Grid>
          {ours.map((b) => (
            <BoardCard
              key={b.id}
              board={b}
              subtitle={b.templateCategory ? t(`boardTemplates.categories.${b.templateCategory}`) : undefined}
              actions={canCopy ? <TemplateActions board={b} org={org} onUse={() => setUsing({ choice: { kind: 'custom', boardId: b.id }, name: b.title })} /> : undefined}
            />
          ))}
        </Grid>
      </Section>
      {using && <UseTemplateDialog org={org} choice={using.choice} name={using.name} onClose={() => setUsing(null)} />}
    </>
  );
}
